/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Grid, Link, Typography } from '@mui/material';
import validate from 'validate.js';
import moment from 'moment';
import * as selectorsJornada from '../../../store/jornada/reducer';
import * as actionsJornada from '../../../store/jornada/actions';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as Role from '../../../constants/role';
import { JORNADA_ITEM_TIPOS } from '../../../constants/jornadaItemTipo';
import {
  DialogDefaultV2,
  InputV2,
  InputFormatV2,
  UsuarioSelectV2,
  OptionSelectV2,
  DatePickerV2,
} from '../../../components/v2';
import { convertTimeToMinutes, isNullOrUndefined, isValidTime, formatToTime } from 'utils';
import {
  buildItensFromTimes,
  computeJornadaTotalMinutes,
  formatEnderecoItem,
  isJornadaOrigemMobile,
  mapsUrlFromItem,
  timesFromItens,
  itensByTipo,
} from '../../../helpers/jornadaHelpers';

const defaultSchema = {
  data: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  ocorrenciaId: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  inspetor: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  inicioJornada: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
};

const validateInvalidTimes = (values) => {
  let errors = {};

  if (!isNullOrUndefined(values.inicioJornada) && !isValidTime(values.inicioJornada)) {
    errors = { ...errors, inicioJornada: ['Horário inválido'] };
  }
  if (!isNullOrUndefined(values.inicioIntervalo) && !isValidTime(values.inicioIntervalo)) {
    errors = { ...errors, inicioIntervalo: ['Horário inválido'] };
  }
  if (!isNullOrUndefined(values.finalIntervalo) && !isValidTime(values.finalIntervalo)) {
    errors = { ...errors, finalIntervalo: ['Horário inválido'] };
  }
  if (!isNullOrUndefined(values.finalJornada) && !isValidTime(values.finalJornada)) {
    errors = { ...errors, finalJornada: ['Horário inválido'] };
  }

  return errors;
};

const hasError = (values, horaExtraDisabled) => {
  let schema = defaultSchema;
  if (!horaExtraDisabled) {
    schema = {
      ...schema,
      horaExtraAutorizadoPor: { presence: { allowEmpty: false, message: 'é obrigatório' } },
      horaExtraJustificativa: { presence: { allowEmpty: false, message: 'é obrigatório' } },
    };
  }

  return {
    ...validate(values, schema),
    ...validateInvalidTimes(values),
  };
};

const emptyValues = (adminPermission, authData) => ({
  data: moment().valueOf(),
  tipoControle: 'FOLHA_PONTO',
  inspetor: !adminPermission ? authData?.id : null,
  itens: [],
});

const Formulario = ({
  open,
  id,
  onClose,
  loadJornada,
  jornada,
  saveJornada,
  authData,
  adminPermission,
}) => {
  const [readOnly, setReadOnly] = useState(!adminPermission);
  const [errors, setErrors] = useState({});
  const [horaExtraDisabled, setHoraExtraDisabled] = useState(true);
  const [values, setValues] = useState(() => emptyValues(adminPermission, authData));

  const origemMobile = useMemo(
    () => isJornadaOrigemMobile(values.itens),
    [values.itens],
  );
  const timesReadOnly = readOnly || (Boolean(id) && origemMobile);
  const byTipo = useMemo(() => itensByTipo(values.itens), [values.itens]);

  const validateHoraExtra = (data, jornadaTotalMinutes) => {
    if (data) {
      const day = (new Date(data)).getDay();
      if (day === 0) {
        setHoraExtraDisabled(false);
        return;
      }
    }

    if (!jornadaTotalMinutes || !data) {
      setHoraExtraDisabled(true);
      return;
    }

    const day = (new Date(data)).getDay();
    let nextHoraExtraDisabled = true;
    if (day === 0) {
      nextHoraExtraDisabled = false;
    } else if (day === 6) {
      nextHoraExtraDisabled = jornadaTotalMinutes <= 4 * 60;
    } else {
      nextHoraExtraDisabled = jornadaTotalMinutes <= 8 * 60;
    }

    setHoraExtraDisabled(nextHoraExtraDisabled);
  };

  useEffect(() => {
    if (id) loadJornada(id);
  }, [id, loadJornada]);

  useEffect(() => {
    if (id && jornada && id === jornada.id) {
      const times = timesFromItens(jornada.itens);
      const next = {
        ...jornada,
        ...times,
        data: moment(parseInt(jornada.data, 10)).valueOf(),
        itens: jornada.itens || [],
      };
      const totalMinutes = computeJornadaTotalMinutes(times, convertTimeToMinutes)
        || jornada.jornadaTotalMinutes;
      setValues({
        ...next,
        jornadaTotalMinutes: totalMinutes,
        jornadaTotal: totalMinutes ? formatToTime(totalMinutes) : (jornada.jornadaTotal || null),
      });
      setReadOnly(!adminPermission && jornada.criadoPor !== authData.id);
      validateHoraExtra(
        moment(parseInt(jornada.data, 10)).valueOf(),
        totalMinutes,
      );
    } else if (!id) {
      setValues(emptyValues(adminPermission, authData));
      setReadOnly(false);
      setHoraExtraDisabled(true);
    }
  }, [jornada, id, adminPermission, authData?.id]);

  const onSave = () => {
    const erros = hasError(values, horaExtraDisabled);
    if (erros != null && Object.keys(erros).length > 0) {
      setErrors(erros);
      return;
    }

    if (id && origemMobile) {
      saveJornada({
        id: values.id,
        ocorrenciaId: values.ocorrenciaId,
        horaExtraJustificativa: values.horaExtraJustificativa || null,
        horaExtraAutorizadoPor: values.horaExtraAutorizadoPor || null,
      }, () => onCloseLocal(true));
      return;
    }

    const itens = buildItensFromTimes(values, values.itens);
    saveJornada({
      id: values.id,
      data: values.data,
      tipoControle: 'FOLHA_PONTO',
      inspetor: values.inspetor,
      inspetorAuxiliar: values.inspetorAuxiliar || null,
      ocorrenciaId: values.ocorrenciaId,
      jornadaTotal: values.jornadaTotal || null,
      horaExtraJustificativa: values.horaExtraJustificativa || null,
      horaExtraAutorizadoPor: values.horaExtraAutorizadoPor || null,
      itens,
    }, () => onCloseLocal(true));
  };

  const onCloseLocal = (isSave) => {
    setErrors({});
    setValues(emptyValues(adminPermission, authData));
    setReadOnly(false);
    setHoraExtraDisabled(true);
    onClose(isSave);
  };

  const onChangeTime = (e, field) => {
    if (timesReadOnly) return;
    const value = e.target.value;
    const nextTimes = {
      inicioJornada: field === 'inicioJornada' ? value : values.inicioJornada,
      inicioIntervalo: field === 'inicioIntervalo' ? value : values.inicioIntervalo,
      finalIntervalo: field === 'finalIntervalo' ? value : values.finalIntervalo,
      finalJornada: field === 'finalJornada' ? value : values.finalJornada,
    };
    const jornadaTotal = computeJornadaTotalMinutes(nextTimes, convertTimeToMinutes);
    const nextValues = {
      ...values,
      [field]: value,
      jornadaTotal: jornadaTotal ? formatToTime(jornadaTotal) : null,
      jornadaTotalMinutes: jornadaTotal,
    };
    setValues(nextValues);
    validateHoraExtra(nextValues.data, jornadaTotal);
  };

  const renderGeoHint = (tipo) => {
    const item = byTipo[tipo];
    if (!item) return null;
    const endereco = formatEnderecoItem(item.enderecoJson);
    const mapsUrl = mapsUrlFromItem(item);
    const coords = item.latitude && item.longitude
      ? `${item.latitude}, ${item.longitude}`
      : null;
    if (!endereco && !coords && !mapsUrl) return null;

    return (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {endereco && (
          <Typography variant="caption" component="div">{endereco}</Typography>
        )}
        {coords && (
          <Typography variant="caption" component="div">
            {coords}
            {item.precisaoMetros ? ` (±${item.precisaoMetros} m)` : ''}
          </Typography>
        )}
        {mapsUrl && (
          <Link href={mapsUrl} target="_blank" rel="noopener noreferrer" variant="caption">
            Ver no Maps
          </Link>
        )}
      </Typography>
    );
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={() => onCloseLocal(false)}
      handlerConfirm={onSave}
      title="Jornada"
      confirmMessage="Salvar"
      maxWidth="md"
      fullWidth
      readOnly={readOnly}
    >
      <Grid container spacing={1} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ pb: 0 }}>
          <DatePickerV2
            label="Data"
            onChange={(data) => {
              setValues({ ...values, data });
              validateHoraExtra(data, values.jornadaTotalMinutes);
            }}
            value={values.data}
            isRequired
            error={!!(errors && errors.data)}
            disabled={readOnly || Boolean(id)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ pb: 0 }}>
          <OptionSelectV2
            label="Ocorrência"
            codigo="PONTO_OCORRENCIA"
            isRequired
            error={!!(errors && errors.ocorrenciaId)}
            onSelect={(ocorrenciaId) => setValues({ ...values, ocorrenciaId })}
            value={values.ocorrenciaId}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ pb: 0 }}>
          <UsuarioSelectV2
            label="Usuário"
            onSelect={(inspetor) => setValues({ ...values, inspetor })}
            isRequired
            error={!!(errors && errors.inspetor)}
            value={values.inspetor}
            disabled={readOnly || !adminPermission || Boolean(id)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ pb: 0 }}>
          <UsuarioSelectV2
            label="Parceiro"
            onSelect={(inspetorAuxiliar) => setValues({ ...values, inspetorAuxiliar })}
            grupos={[Role.ADMIN, Role.INSPETOR]}
            value={values.inspetorAuxiliar}
            disabled={readOnly || Boolean(id)}
          />
        </Grid>
        {JORNADA_ITEM_TIPOS.map(({ tipo, timeField, label }) => (
          <Grid key={tipo} size={{ xs: 12, sm: 4 }} sx={{ pb: 0 }}>
            <InputFormatV2
              label={label}
              value={values[timeField]}
              onChange={(e) => onChangeTime(e, timeField)}
              format="##:##"
              placeholder="HH:MM"
              disabled={timesReadOnly}
              isRequired={timeField === 'inicioJornada'}
              error={!!(errors && errors[timeField])}
              helperText={errors && errors[timeField] ? errors[timeField] : null}
            />
            {renderGeoHint(tipo)}
          </Grid>
        ))}
        <Grid size={{ xs: 12, sm: 4 }} sx={{ pb: 0 }}>
          <InputV2 label="Hora Total Trabalhada" disabled value={values.jornadaTotal || ''} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ pb: 0 }}>
          <UsuarioSelectV2
            label="Hora Extra Autorizado Por"
            onSelect={(horaExtraAutorizadoPor) => setValues({
              ...values,
              horaExtraAutorizadoPor,
            })}
            grupos={[Role.ADMIN, Role.SUPER_ADMIN]}
            value={values.horaExtraAutorizadoPor}
            disabled={horaExtraDisabled || readOnly}
            isRequired={!horaExtraDisabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ pb: 0 }}>
          <InputV2
            label="Justificativa Hora Extra"
            name="horaExtraJustificativa"
            onChange={(event) => setValues({
              ...values,
              horaExtraJustificativa: event.target.value,
            })}
            value={values.horaExtraJustificativa || ''}
            rows={3}
            multiline
            disabled={horaExtraDisabled || readOnly}
            isRequired={!horaExtraDisabled}
          />
        </Grid>
      </Grid>
    </DialogDefaultV2>
  );
};

Formulario.propTypes = {
  open: PropTypes.bool.isRequired,
  id: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  loadJornada: PropTypes.func.isRequired,
  jornada: PropTypes.object,
  saveJornada: PropTypes.func.isRequired,
  authData: PropTypes.object.isRequired,
  adminPermission: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  jornada: selectorsJornada.getSelectedJornada(state),
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actionsJornada,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Formulario));
